import type { JobOpportunity } from '../../state/jobOpportunities'
import { FALLBACK_JOB_STATUS_LABELS } from '../../state/jobOpportunities'

// MWA-251 — fixed, locked tab set (states intentionally not admin-configurable
// for this flow). Shared by the staff (JobApplicationsTracker) and portal
// (PortalJobApplications) Job Search pages so the two can't drift apart — each
// must show a job under the same tab. The two entity tabs (Target Companies,
// Executive Search) are NOT part of this set: they aren't statuses, and each
// page renders them itself alongside these.
//
export type JobTabKey =
  | 'hidden_opportunity'
  | 'pending_decision'
  | 'applied'
  | 'following_up'
  | 'interviewing'
  | 'got_the_job'
  | 'rejected'
  | 'closed_not_accepting'
  | 'archived'

export interface JobTabDefinition {
  key: JobTabKey
  label: string
  // Overrides `label` for the client portal — see jobTabLabel() below. Only
  // set where the staff wording actually reads badly to the client it's
  // about; the rest already use the client's own status vocabulary verbatim.
  clientLabel?: string
}

// Labels for the six status tabs are pulled from FALLBACK_JOB_STATUS_LABELS
// (the client's own Momentum Maximizer Status dropdown, adopted in Task 2)
// rather than retyped here. Pending Client Decision and Archived are derived,
// not statuses, so they carry their own label.
//
// `pending_decision`'s staff label describes the client in the third person
// ("Pending Client Decision"); a client reading their own queue needs the
// first-person framing already used elsewhere on the portal (PortalDashboard's
// "Awaiting your decision" card, the decision filter's "Awaiting your decision"
// option) rather than a new phrase invented just for this tab.
export const JOB_TABS: JobTabDefinition[] = [
  // MWA-304 — the earliest point on the board: a seat we believe is opening,
  // before anyone has applied to anything. Read-only for the client, so it
  // deliberately carries no clientLabel; both audiences call it the same thing.
  { key: 'hidden_opportunity', label: 'Hidden Opportunities' },
  { key: 'pending_decision', label: 'Pending Client Decision', clientLabel: 'Awaiting Your Decision' },
  { key: 'applied', label: FALLBACK_JOB_STATUS_LABELS.applied },
  { key: 'following_up', label: FALLBACK_JOB_STATUS_LABELS.following_up },
  { key: 'interviewing', label: FALLBACK_JOB_STATUS_LABELS.interviewing },
  { key: 'got_the_job', label: FALLBACK_JOB_STATUS_LABELS.got_the_job },
  { key: 'rejected', label: FALLBACK_JOB_STATUS_LABELS.rejected },
  { key: 'closed_not_accepting', label: FALLBACK_JOB_STATUS_LABELS.closed_not_accepting },
  { key: 'archived', label: 'Archived' },
]

// Tab label for the given audience — staff sees `label`; the client portal
// prefers `clientLabel` where one is defined, falling back to `label` for the
// tabs whose wording already reads fine either way.
export function jobTabLabel(tab: JobTabDefinition, audience: 'staff' | 'client'): string {
  return (audience === 'client' ? tab.clientLabel : undefined) ?? tab.label
}

// MWA-281 — tabs where the inactivity nudge doesn't apply: the application has
// reached an end state (accepted, rejected, or closed out) or sits in the
// archive. Centralized so a newly added terminal tab can't be missed by the
// inactivity-chip gate in JobApplicationsTracker / PortalJobApplications, the
// way 'rejected' and 'got_the_job' were the first time around.
export const TERMINAL_JOB_TABS: readonly JobTabKey[] = [
  'got_the_job',
  'rejected',
  'closed_not_accepting',
  'archived',
]

// Assign a job to exactly one tab.
// - Archived ("Not interested", MWA-220) always wins.
// - An opportunity with no status, or one awaiting the client's decision
//   (MWA-220), sits in Pending Client Decision.
// - Otherwise it falls under its pipeline status.
export function jobTabKey(j: JobOpportunity): JobTabKey {
  if (j.archived) return 'archived'
  // MWA-304 — checked BEFORE the statusless branch below, and the order is the
  // entire feature. A hidden opportunity carries no status either, so reversing
  // these two files every one of them under Pending Client Decision, which the
  // portal renders as "Awaiting Your Decision" — the opposite of the read-only
  // notice this is meant to be. Gated on the absence of a status so that
  // converting one (staff set a status) hands it back to the pipeline below.
  if (j.hiddenOpportunity && !j.status) return 'hidden_opportunity'
  // Checked before decisionRequested, and separately from it. A staff-created
  // opportunity carries no status until the client answers, and the answer
  // writes the status in the same operation — so a record with no status and an
  // answered decision means that write was interrupted. It has to stay visible
  // and actionable rather than fall through the status switch, which is where
  // the old terminal default filed it under Closed / Not Accepting.
  if (!j.status) return 'pending_decision'
  if (j.decisionRequested && !j.applyDecision) return 'pending_decision'
  switch (j.status) {
    case 'applied':
      return 'applied'
    case 'following_up':
      return 'following_up'
    case 'interviewing':
      return 'interviewing'
    case 'got_the_job':
      return 'got_the_job'
    case 'rejected':
      return 'rejected'
    case 'closed_not_accepting':
      return 'closed_not_accepting'
    // Transitional — the Task 3 migration that rewrites existing 'offer'/'closed'
    // records to the new vocabulary is deliberately not run in this branch, so
    // live records may still hold these two retired values. Route them to their
    // nearest new-vocabulary equivalent so they stay visible instead of matching
    // no tab. Remove this pair once the migration ships.
    case 'offer':
      return 'got_the_job'
    case 'closed':
      return 'closed_not_accepting'
    // Transitional — 'applying' was the entry status meaning "awaiting the
    // client's decision", before that became the absence of a status. Live
    // records hold it until the migration runs, and what they are waiting for
    // has not changed, so they belong in the same tab. This also makes the
    // deploy safe in either order: the code can land before the migration
    // without a single job moving to the wrong tab. Remove once it has run.
    case 'applying':
      return 'pending_decision'
    // An unrecognised status is a data defect, not a closed opportunity.
    // Returning 'closed_not_accepting' here declared live applications dead and
    // hid them from staff and client alike; 'applied' keeps such a record
    // visible and actionable. Both write paths validate status against
    // JOB_STATUSES, so nothing should be able to produce one.
    default:
      return 'applied'
  }
}
